import {
  forwardRef,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Valor técnico (URL, token, ID): aplica fonte mono. */
  mono?: boolean;
  invalid?: boolean;
  leading?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, mono, invalid, leading, ...props },
  ref,
) {
  return (
    <div
      className={cn(
        'flex h-control items-center gap-2 rounded border border-border-strong bg-surface-1 px-pad-x text-base text-text-1 transition-[border-color,box-shadow] duration-(--dur-fast) ease-app focus-within:border-accent focus-within:shadow-glow has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-surface-2 has-[input:disabled]:text-text-2',
        invalid && 'border-danger focus-within:border-danger focus-within:shadow-none',
        mono && 'font-mono',
        className,
      )}
    >
      {leading ? <span className="shrink-0 text-text-3 [&_svg]:size-4">{leading}</span> : null}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-text-3 disabled:cursor-not-allowed"
        {...props}
      />
    </div>
  );
});

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('block text-sm font-medium text-text-2', className)} {...props} />;
}

export function FieldError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="text-sm text-danger">
      {children}
    </p>
  );
}

export function Field({
  label,
  htmlFor,
  error,
  children,
  hint,
}: {
  label: string;
  htmlFor?: string;
  error?: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <FieldError>{error}</FieldError>
      ) : hint ? (
        <p className="text-sm text-text-3">{hint}</p>
      ) : null}
    </div>
  );
}
